import { AiAvatar } from './AiAvatar';

/**
 * The far-left tool rail (spec 35f) — ported in spirit from Sparked v2's course-editor tool column.
 * Two groups: PANELS (which content panel is open on the right) and TOOLS (the workspace interaction
 * mode). Mode-dependent — this is the Build-mode set; Design mode gets its own later.
 */

export type StudioPanel = 'ai' | 'media' | 'sprites' | 'codes' | 'history' | 'team';
/** 'none' = no workspace tool active (the preview plays normally); tools toggle off on re-click. */
export type StudioTool = 'none' | 'select' | 'grab' | 'text' | 'snapshot';

interface ToolRailProps {
  panel: StudioPanel;
  tool: StudioTool;
  onPanel: (p: StudioPanel) => void;
  onTool: (t: StudioTool) => void;
  teamUnread?: number;
  /** a build/edit is running — the tools that write to the app are locked for the duration */
  busy?: boolean;
}

/* ---- icons ---- */
const IconMedia = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <circle cx="8.5" cy="9.5" r="1.6" />
    <path d="M4 17l4.5-4.5a2 2 0 0 1 2.8 0L20 20" />
  </svg>
);
const IconSprite = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 3v18M15 3v18M3 9h18M3 15h18" />
  </svg>
);
const IconCode = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 8l-4 4 4 4M16 8l4 4-4 4M13 5l-2 14" />
  </svg>
);
const IconHistory = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8m0-5v5h5" />
    <path d="M12 7v5l3.5 2" />
  </svg>
);
const IconTeam = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="8" r="3" />
    <path d="M3.5 20a5.5 5.5 0 0 1 11 0M16 6.5a3 3 0 0 1 0 5.8M18 20a5.5 5.5 0 0 0-3-4.9" />
  </svg>
);
const IconSelect = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 3l14 8-6 1.5L12 20 6 3z" />
  </svg>
);
const IconGrab = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 12V6a1.5 1.5 0 0 1 3 0v5m0-5.5a1.5 1.5 0 0 1 3 0V11m0-4a1.5 1.5 0 0 1 3 0v6a6 6 0 0 1-6 6h-1a6 6 0 0 1-5.2-3L5 13.5A1.5 1.5 0 0 1 7.6 12l.9 1.2" />
  </svg>
);
const IconText = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 6V4h14v2M12 4v16M9 20h6" />
  </svg>
);
const IconSnapshot = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 7h3l1.5-2h7L18 7h2a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z" />
    <circle cx="12" cy="13" r="3.2" />
  </svg>
);

function RailButton({
  active,
  onClick,
  label,
  badge,
  disabled,
  disabledHint,
  children,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  badge?: number;
  disabled?: boolean;
  disabledHint?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled && disabledHint ? disabledHint : label}
      aria-label={label}
      aria-pressed={active}
      className={[
        'relative grid h-11 w-11 shrink-0 place-items-center rounded-lg transition lg:h-10 lg:w-10',
        disabled
          ? 'cursor-not-allowed text-muted/40'
          : active
            ? 'bg-accent/15 text-accent-deep'
            : 'text-muted hover:bg-page hover:text-ink',
      ].join(' ')}
    >
      {children}
      {active && (
        <>
          {/* the active marker rides the edge the strip is attached to: bottom on mobile, left on desktop */}
          <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-accent lg:hidden" />
          <span className="absolute left-[-9px] top-1/2 hidden h-5 w-1 -translate-y-1/2 rounded-full bg-accent lg:block" />
        </>
      )}
      {badge !== undefined && badge > 0 && (
        <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-[16px] place-items-center rounded-full bg-danger px-1 text-[9px] font-bold text-white">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </button>
  );
}

export function StudioToolRail({ panel, tool, onPanel, onTool, teamUnread = 0, busy = false }: ToolRailProps) {
  return (
    /*
      A vertical column of icons is a desktop idiom; on a phone it steals a whole edge of a small
      screen. Below lg it becomes a horizontal, scrollable strip under the toolbar — the shape a
      native app would use — and the border disappears with the rest of the chrome.
    */
    <div className="flex shrink-0 items-center gap-1 overflow-x-auto bg-surface px-2 py-2 lg:flex-col lg:overflow-visible lg:border-r lg:border-line lg:py-3">
      {/* AI Collaborator — the animated Sparky orb */}
      <button
        type="button"
        onClick={() => onPanel('ai')}
        title="AI Collaborator"
        aria-label="AI Collaborator"
        aria-pressed={panel === 'ai'}
        className={[
          'relative grid h-11 w-11 shrink-0 place-items-center rounded-lg transition',
          panel === 'ai' ? 'bg-accent/15' : 'hover:bg-page',
        ].join(' ')}
      >
        <AiAvatar size={30} />
        {panel === 'ai' && (
          <>
            <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-accent lg:hidden" />
            <span className="absolute left-[-9px] top-1/2 hidden h-5 w-1 -translate-y-1/2 rounded-full bg-accent lg:block" />
          </>
        )}
      </button>

      <RailButton active={panel === 'media'} onClick={() => onPanel('media')} label="Media">
        <IconMedia />
      </RailButton>
      <RailButton active={panel === 'sprites'} onClick={() => onPanel('sprites')} label="Sprite generator">
        <IconSprite />
      </RailButton>
      <RailButton active={panel === 'codes'} onClick={() => onPanel('codes')} label="Codes">
        <IconCode />
      </RailButton>
      <RailButton active={panel === 'history'} onClick={() => onPanel('history')} label="History">
        <IconHistory />
      </RailButton>
      <RailButton active={panel === 'team'} onClick={() => onPanel('team')} label="Team" badge={teamUnread}>
        <IconTeam />
      </RailButton>

      <div className="mx-1 h-6 w-px shrink-0 bg-line lg:my-1.5 lg:mx-0 lg:h-px lg:w-6" />

      {/*
        Interaction tools. Select and Edit text are locked while a build or edit is running: both
        act ON the app — Select hands an element to the next prompt, Edit text writes straight back
        to the source — and the app is being rewritten underneath them. Anything picked mid-run
        points at a version that is about to be replaced. Pan and Snapshot only READ the preview, so
        they stay available for watching the rebuild.
      */}
      <RailButton
        active={tool === 'select'}
        onClick={() => onTool('select')}
        label="Select"
        disabled={busy}
        disabledHint="Select is unavailable while your app is being changed"
      >
        <IconSelect />
      </RailButton>
      <RailButton active={tool === 'grab'} onClick={() => onTool('grab')} label="Grab / pan">
        <IconGrab />
      </RailButton>
      <RailButton
        active={tool === 'text'}
        onClick={() => onTool('text')}
        label="Edit text"
        disabled={busy}
        disabledHint="Text editing is unavailable while your app is being changed"
      >
        <IconText />
      </RailButton>
      <RailButton active={tool === 'snapshot'} onClick={() => onTool('snapshot')} label="Snapshot & notes">
        <IconSnapshot />
      </RailButton>
    </div>
  );
}
